// 🔧 firebase.js
// ✅ Purpose: Central Firebase client SDK setup for LGV
// ✅ Features:
// - Initializes Firebase App
// - Initializes Auth, Firestore, Storage, and Functions
// - Supports Mission Control runtime for fully local emulator development
// - Connects React app to local Auth, Firestore, Functions, and Storage emulators
// - Keeps DEV/PROD behavior unchanged unless REACT_APP_RUNTIME=mission-control
// - Exposes Firebase helpers on window for console debugging
// - Fully block-commented for clarity and future devs

import { initializeApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
} from "firebase/app-check";

import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  connectAuthEmulator,
} from "firebase/auth";

import {
  initializeFirestore,
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  connectFirestoreEmulator,
} from "firebase/firestore";

import {
  getStorage,
  connectStorageEmulator,
} from "firebase/storage";

import {
  getFunctions,
  connectFunctionsEmulator,
} from "firebase/functions";

import { logger } from "./utils/logger";

// =====================================================
// 🔥 Firebase Config
// =====================================================
// ✅ These values still come from .env files.
// ✅ The projectId can remain let-s-go-vote---dev for Mission Control.
// ✅ The SDK will redirect traffic to local emulators when enabled.
// =====================================================
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "demo-api-key",
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    "let-s-go-vote---dev.firebaseapp.com",
  projectId:
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    "let-s-go-vote---dev",
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    "let-s-go-vote---dev.appspot.com",
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "demo-sender-id",
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID || "demo-app-id",
};

// =====================================================
// 🧪 Runtime Mode
// =====================================================
// ✅ mission-control = React uses local Firebase emulators
// ✅ dev/prod/local without mission-control = React uses configured Firebase project
// =====================================================
const runtime =
  process.env.REACT_APP_RUNTIME ||
  process.env.REACT_APP_ENV ||
  "dev";

const useMissionControl =
  runtime === "mission-control" ||
  process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true";

console.log("Firebase Config:", {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  storageBucket: firebaseConfig.storageBucket,
  runtime,
  useMissionControl,
});

// =====================================================
// 🚀 Initialize Firebase
// =====================================================
const app = initializeApp(firebaseConfig);

// App Check tokens are collected only after a production site key has been
// configured. Enforcement remains a separate Firebase Console rollout so live
// traffic can be monitored before requests are rejected.
const appCheckSiteKey =
  process.env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY || "";

if (!useMissionControl && appCheckSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

const auth = getAuth(app);

const db = initializeFirestore(app, {
  // experimentalForceLongPolling: true,
});

export const storage = getStorage(app);

export const functions = getFunctions(app, "us-central1");

// =====================================================
// 🧭 Mission Control Emulator Bridge
// =====================================================
// ✅ This is the missing bridge between npm start and local emulators.
// ✅ Tests already use emulator env vars through scripts/dev/run-with-emulators.js.
// ✅ This block makes the browser do the same thing.
// =====================================================
if (useMissionControl) {
  console.log("🧪 Mission Control active: connecting React to Firebase emulators");

  connectAuthEmulator(auth, "http://127.0.0.1:9099", {
    disableWarnings: true,
  });

  connectFirestoreEmulator(db, "127.0.0.1", 8080);

  connectFunctionsEmulator(functions, "127.0.0.1", 5001);

  connectStorageEmulator(storage, "127.0.0.1", 9199);
}

const googleProvider = new GoogleAuthProvider();

// =====================================================
// 🔐 Google Sign-In
// =====================================================
const signInWithGoogle = async () => {
  try {
    const res = await signInWithPopup(auth, googleProvider);
    const user = res.user;

    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        uid: user.uid,
        name: user.displayName,
        firstName: user.displayName?.split(" ")[0] || "",
        lastName: user.displayName?.split(" ").slice(1).join(" ") || "",
        authProvider: "google",
        email: user.email,
        role: "voter",
      });

      logger.log("✅ Google user doc created:", {
        authenticated: true,
      });
    } else {
      logger.log("ℹ️ Google user already exists:", {
        authenticated: true,
      });
    }

    return res;
  } catch (err) {
    logger.error("❌ Google sign-in error:", {
      value: err,
    });

    alert(err.message);
    return null;
  }
};

// =====================================================
// 🔐 Email Login
// =====================================================
const logInWithEmailAndPassword = async (email, password) => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    logger.error("❌ Email login failed:", { value: err });
    alert(err.message);
  }
};

// =====================================================
// 📝 Email Registration
// =====================================================
const registerWithEmailAndPassword = async (
  name,
  email,
  password,
  phone,
  showing
) => {
  try {
    const res = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    const user = res.user;

    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      name,
      authProvider: "local",
      email,
      phone,
      showing,
      role: "voter",
    });

    logger.log("✅ User document created with UID:", {
      authenticated: true,
    });
  } catch (err) {
    logger.error("❌ Error during registration:", {
      value: err,
    });

    alert(err.message);
  }
};

// =====================================================
// 🔑 Password Reset
// =====================================================
const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    alert("Password reset link sent!");
  } catch (err) {
    logger.error("❌ Password reset request failed:", { value: err });
    alert(err.message);
  }
};

// =====================================================
// 👤 Role Helper
// =====================================================
export const getUserRole = async (uid, eventId = null) => {
  const globalRef = doc(db, "users", uid);
  const globalSnap = await getDoc(globalRef);
  const globalData = globalSnap.data();

  logger.log("📥 Resolving user role", { eventId: eventId || null });

  if (globalData?.role === "admin") {
    return "admin";
  }

  if (eventId) {
    const eventRef = doc(db, "events", eventId, "users", uid);
    const eventSnap = await getDoc(eventRef);

    return eventSnap.exists() ? eventSnap.data().role : null;
  }

  return globalData?.role || null;
};

// =====================================================
// 🧰 Console Debug Helpers
// =====================================================
if (runtime !== "prod" && runtime !== "production") {
  window.db = db;
  window.auth = auth;
  window.getDoc = getDoc;
  window.doc = doc;
  window.collection = collection;
  window.getDocs = getDocs;
}

export {
  auth,
  db,
  signInWithGoogle,
  logInWithEmailAndPassword,
  registerWithEmailAndPassword,
  sendPasswordReset,
};
